import React from "react";
import { X, Plus, Trash2, Loader2, IndianRupee } from "lucide-react";

// Convert number to Indian English words representation
function numberToWords(num) {
  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const convert = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
    if (n < 1000) return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " and " + convert(n % 100) : "");
    if (n < 100000) return convert(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + convert(n % 1000) : "");
    if (n < 10000000) return convert(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + convert(n % 100000) : "");
    return convert(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + convert(n % 10000000) : "");
  };

  if (num === 0) return "Zero";
  return convert(num);
}

export function PaySetupFormModal({ 
  isOpen, onClose, employee, formData, formErrors, computedTotals, 
  onUpdateGross, onUpdateField, onUpdateComponent, onAddComponent, onRemoveComponent, onSubmit, isSaving 
}) {
  if (!isOpen || !employee) return null;

  // Split components into earnings and deductions in memory with their original indices
  const earnings = (formData.components || [])
    .map((c, i) => ({ ...c, originalIndex: i }))
    .filter((c) => c.type === "earning");

  const deductions = (formData.components || [])
    .map((c, i) => ({ ...c, originalIndex: i }))
    .filter((c) => c.type === "deduction");

  const wordsText = computedTotals.net > 0 
    ? `Indian Rupees ${numberToWords(Math.max(0, computedTotals.net))} Only`
    : "Zero Rupees Only";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop blur */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      
      {/* Modal Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col animate-in fade-in zoom-in-95 duration-200 border border-slate-100">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="text-[15px] font-medium text-slate-800">Configure Pay Setup — {employee.name}</h2>
            <p className="text-[12px] text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              <span>ID: <strong className="font-medium text-slate-700">#{employee.id}</strong></span>
              <span>• Phone: <strong className="font-medium text-slate-700">{employee.phone || "N/A"}</strong></span>
              <span>• Joined: <strong className="font-medium text-slate-700">{employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString("en-IN") : "N/A"}</strong></span>
              <span>• Role: <strong className="font-medium text-slate-700 capitalize">{employee.designation?.replace("_", " ")}</strong></span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* General Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-sm font-semibold text-slate-700">Gross Salary (₹/month) *</label>
                {formData.grossSalary && !isNaN(Number(formData.grossSalary)) && (
                  <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100/50">
                    {((Number(formData.grossSalary) * 12) / 100000).toFixed(2)} LPA
                  </span>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">₹</span>
                <input
                  type="number" 
                  className={`input w-full pl-7 text-sm py-2 rounded-md border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 ${formErrors.grossSalary ? "border-red-400 focus:ring-red-100" : ""}`}
                  placeholder="e.g. 600000" 
                  value={formData.grossSalary || ""}
                  onChange={(e) => onUpdateGross(e.target.value)}
                />
              </div>
              {formErrors.grossSalary && <p className="text-red-500 text-xs mt-1.5">{formErrors.grossSalary}</p>}
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-semibold text-slate-700">Pay Frequency</label>
              <select className="input w-full py-2 bg-slate-50 text-slate-500 cursor-not-allowed text-sm rounded-md border-slate-200" value={formData.frequency || "monthly"} disabled>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-semibold text-slate-700">PF Number</label>
              <input
                type="text" 
                className="input w-full text-sm py-2 rounded-md border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                placeholder="e.g. AP/HYD/0012345/000/0000123" 
                value={formData.pfNumber || ""}
                onChange={(e) => onUpdateField("pfNumber", e.target.value)}
              />
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-semibold text-slate-700">ESI Number</label>
              <input
                type="text" 
                className="input w-full text-sm py-2 rounded-md border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                placeholder="e.g. 51000000000000000" 
                value={formData.esiNumber || ""}
                onChange={(e) => onUpdateField("esiNumber", e.target.value)}
              />
            </div>

            {/* Bonus Section (Outside Gross) */}
            <div className="md:col-span-2 mt-1 p-4 bg-indigo-50/50 rounded-xl border border-indigo-100/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-semibold text-indigo-800">Bonus (₹/year)</label>
                  <span className="text-[10px] font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded border border-indigo-200">
                    OUTSIDE GROSS
                  </span>
                </div>
                <p className="text-xs text-indigo-600/70 font-medium">Calculated and paid yearly from the date of joining.</p>
              </div>
              <div className="relative w-full md:w-64 flex-shrink-0">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">₹</span>
                <input
                  type="number" 
                  className="input w-full pl-7 text-sm py-2 rounded-md border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                  placeholder="e.g. 2500" 
                  value={formData.bonus || ""}
                  onChange={(e) => onUpdateField("bonus", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Side-by-Side Earnings and Deductions */}
          <div>
            <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-3">
              <h3 className="text-sm font-semibold text-slate-700">Salary Component Details</h3>
              {formErrors.components && (
                <p className="text-red-500 text-xs font-medium">{formErrors.components}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Earnings Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-t-md px-3 py-2.5">
                  <span className="text-xs font-semibold text-blue-700">Earnings</span>
                  <span className="text-xs font-semibold text-slate-500">Amount (₹)</span>
                </div>

                <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                  {earnings.length === 0 ? (
                    <p className="text-center text-sm text-slate-400 py-6 border border-dashed border-slate-200 rounded-md">No earnings defined</p>
                  ) : (
                    earnings.map((comp) => (
                      <div key={comp.originalIndex} className="flex items-center gap-2 group">
                        <input
                          className="input flex-1 bg-white text-sm py-2 px-3 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 rounded-md"
                          placeholder="e.g. Basic Salary"
                          value={comp.name || ""} 
                          onChange={(e) => onUpdateComponent(comp.originalIndex, "name", e.target.value)}
                        />
                        <input
                          type="number" 
                          className="input w-28 text-right bg-white text-sm py-2 px-3 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 rounded-md"
                          placeholder="0" 
                          value={comp.amount || 0}
                          onChange={(e) => onUpdateComponent(comp.originalIndex, "amount", e.target.value)}
                        />
                        <button
                          onClick={() => onRemoveComponent(comp.originalIndex)}
                          className="p-1.5 rounded-md hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                          title="Remove Component"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <button 
                  onClick={() => onAddComponent("earning")}
                  className="text-sm text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1.5 py-1 transition-colors w-fit"
                >
                  <Plus className="w-4 h-4" /> Add Earnings Row
                </button>
              </div>

              {/* Deductions Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-t-md px-3 py-2.5">
                  <span className="text-xs font-semibold text-red-600">Deductions</span>
                  <span className="text-xs font-semibold text-slate-500">Amount (₹)</span>
                </div>

                <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                  {deductions.length === 0 ? (
                    <p className="text-center text-sm text-slate-400 py-6 border border-dashed border-slate-200 rounded-md">No deductions defined</p>
                  ) : (
                    deductions.map((comp) => (
                      <div key={comp.originalIndex} className="flex items-center gap-2 group">
                        <input
                          className="input flex-1 bg-white text-sm py-2 px-3 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 rounded-md"
                          placeholder="e.g. Provident Fund"
                          value={comp.name || ""} 
                          onChange={(e) => onUpdateComponent(comp.originalIndex, "name", e.target.value)}
                        />
                        <input
                          type="number" 
                          className="input w-28 text-right bg-white text-sm py-2 px-3 border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200 rounded-md"
                          placeholder="0" 
                          value={comp.amount || 0}
                          onChange={(e) => onUpdateComponent(comp.originalIndex, "amount", e.target.value)}
                        />
                        <button
                          onClick={() => onRemoveComponent(comp.originalIndex)}
                          className="p-1.5 rounded-md hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                          title="Remove Component"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <button 
                  onClick={() => onAddComponent("deduction")}
                  className="text-sm text-red-500 hover:text-red-600 font-semibold flex items-center gap-1.5 py-1 transition-colors w-fit"
                >
                  <Plus className="w-4 h-4" /> Add Deductions Row
                </button>
              </div>

            </div>

            {/* Column Totals Summary Bar */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-slate-100 pt-5 mt-5">
              <div className="flex justify-between items-center text-sm font-semibold text-slate-700 bg-slate-50 p-3 rounded-md border border-slate-100">
                <span>Gross Earnings</span>
                <span className="text-emerald-600">₹{Number(computedTotals.earnings).toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between items-center text-sm font-semibold text-slate-700 bg-slate-50 p-3 rounded-md border border-slate-100">
                <span>Total Deductions</span>
                <span className="text-red-500">₹{Number(computedTotals.deductions).toLocaleString("en-IN")}</span>
              </div>
            </div>

            {/* Employer Contributions */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 mt-5 mb-1">
              <h4 className="text-sm font-semibold text-slate-700 mb-3 border-b border-slate-200 pb-2">Employer Contributions (Monthly)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Employer PF Rate config */}
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs font-semibold text-slate-600">Employer PF Rate (%)</span>
                    <span className="text-xs font-bold text-slate-800">₹{Number(computedTotals.employerPf).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      className="input w-full text-sm py-1.5 rounded-md border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                      value={formData.employerPfRate}
                      onChange={(e) => onUpdateField("employerPfRate", e.target.value)}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-medium">%</span>
                  </div>
                </div>

                {/* Employer ESI Rate config */}
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs font-semibold text-slate-600">Employer ESI Rate (%)</span>
                    <span className="text-xs font-bold text-slate-800">₹{Number(computedTotals.employerEsi).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      className="input w-full text-sm py-1.5 rounded-md border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                      value={formData.employerEsiRate}
                      onChange={(e) => onUpdateField("employerEsiRate", e.target.value)}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-medium">%</span>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Total Net Payable Banner */}
          <div className="bg-blue-50/50 rounded-xl p-5 border border-blue-100 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm mb-1">
            <div>
              <h4 className="text-sm font-bold text-blue-700 flex items-center gap-1.5">
                <IndianRupee className="w-4 h-4" />
                Total Net Payable
              </h4>
              <p className="text-xs text-slate-500 mt-1 font-medium">Gross Earnings - Total Deductions</p>
              <p className="text-xs text-blue-700 font-semibold mt-2.5 italic bg-white px-3 py-1.5 rounded border border-blue-100 w-fit select-all">
                {wordsText}
              </p>
            </div>
            <div className="md:text-right flex-shrink-0 flex items-center md:justify-end gap-1.5 select-all">
              <span className="text-2xl font-bold text-blue-700 leading-none">
                ₹{Number(Math.max(0, computedTotals.net)).toLocaleString("en-IN")}
              </span>
              <span className="text-xs font-semibold text-slate-400 mt-2">/ Month</span>
            </div>
          </div>

          {/* CTC Banner */}
          <div className="bg-emerald-50/50 rounded-xl p-5 border border-emerald-100 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
            <div>
              <h4 className="text-sm font-bold text-emerald-700 flex items-center gap-1.5">
                <IndianRupee className="w-4 h-4" />
                Cost to Company (CTC)
              </h4>
              <p className="text-xs text-emerald-600/80 mt-1 font-medium">Includes Yearly Gross, Employer Contributions & Bonus</p>
            </div>
            <div className="md:text-right flex-shrink-0 flex items-center md:justify-end gap-1.5 select-all">
              <span className="text-2xl font-bold text-emerald-700 leading-none">
                ₹{Number(Math.max(0, computedTotals.ctc)).toLocaleString("en-IN")}
              </span>
              <span className="text-xs font-semibold text-slate-400 mt-2">/ Year</span>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3 flex-shrink-0">
          <button onClick={onClose} className="btn-secondary" disabled={isSaving}>Cancel</button>
          <button onClick={onSubmit} className="btn-primary min-w-[120px] shadow-sm" disabled={isSaving}>
            {isSaving ? (
              <span className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...</span>
            ) : "Save Pay Setup"}
          </button>
        </div>

      </div>
    </div>
  );
}
